import React, { useState, useEffect } from 'react';
import { ShieldCheck, Cookie } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getLocalizedPath } from '../utils/router';

interface CookieConsentBannerProps {
  onNavigate: (path: string) => void;
}

export default function CookieConsentBanner({ onNavigate }: CookieConsentBannerProps) {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    try {
      const consent = localStorage.getItem('kviksign_cookie_consent');
      if (!consent) {
        // Small initial delay so page loads smoothly first
        const timer = setTimeout(() => setIsVisible(true), 800);
        return () => clearTimeout(timer);
      }
    } catch {
      // Ignore storage errors in restricted iframes
    }
  }, []);

  const handleAcceptAll = () => {
    try {
      localStorage.setItem('kviksign_cookie_consent', 'all');
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
          ad_storage: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted',
        });
      }
      if (typeof (window as any).clarity === 'function') {
        (window as any).clarity('consent');
      }
      if (typeof (window as any).fbq === 'function') {
        (window as any).fbq('consent', 'grant');
      }
    } catch {
      // Storage unavailable
    }
    setIsVisible(false);
  };

  const handleAcceptEssential = () => {
    try {
      localStorage.setItem('kviksign_cookie_consent', 'essential');
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        });
      }
      if (typeof (window as any).fbq === 'function') {
        (window as any).fbq('consent', 'revoke');
      }
    } catch {
      // Storage unavailable
    }
    setIsVisible(false);
  };

  if (!isVisible) return null;

  const isEn = language === 'en';

  return (
    <div 
      role="region" 
      aria-label="Cookie consent banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-md z-50 animate-fade-in"
    >
      <div className="bg-white/98 backdrop-blur-md rounded-2xl border border-slate-200/90 shadow-2xl p-5 text-slate-800">
        <div className="flex items-start gap-3.5 mb-3">
          <div className="h-9 w-9 rounded-xl bg-blue-50 text-[#0050ff] flex items-center justify-center shrink-0">
            <Cookie className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900 leading-tight">
              {isEn ? 'Cookies & Privacy at KvikSign' : 'Cookies & Privatliv hos KvikSign'}
            </h3>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              {isEn ? (
                <>
                  We use cookies for technical stability and anonymous visit statistics via GA4 to improve your experience. See our{' '}
                  <a
                    href={getLocalizedPath('/privacy', language)}
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigate(getLocalizedPath('/privacy', language));
                    }}
                    className="text-[#0050ff] hover:underline font-medium"
                  >
                    Privacy Policy
                  </a>.
                </>
              ) : (
                <>
                  Vi anvender cookies til teknisk drift og anonym statistik via Google Analytics for at forbedre platformen. Læs vores{' '}
                  <a
                    href={getLocalizedPath('/privacy', language)}
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigate(getLocalizedPath('/privacy', language));
                    }}
                    className="text-[#0050ff] hover:underline font-medium"
                  >
                    privatlivspolitik
                  </a>.
                </>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            onClick={handleAcceptEssential}
            className="px-3.5 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            {isEn ? 'Only essential' : 'Kun nødvendige'}
          </button>
          <button
            onClick={handleAcceptAll}
            className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-[#0050ff] hover:bg-[#003cb3] transition-colors shadow-sm cursor-pointer flex items-center gap-1.5"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            {isEn ? 'Accept all' : 'Accepter alle'}
          </button>
        </div>
      </div>
    </div>
  );
}
